import { lazy, LazyExoticComponent, ReactComponentElement } from "react";
import DataTypes from "~data";

import { getRouteOrigin, path, PathParameters } from "./paths";
import { NotFound } from "./router";

export type RouteType = {
  auth?: RouteAuthType;
  bypassAuthForAdmin?: boolean;
  children?: Array<[PathParameters, RouteType]>;
  component: LazyExoticComponent<() => ReactComponentElement<any>> | typeof NotFound;
};
type PermissionKeys = keyof DataTypes["TeamMemberships"]["permissions"];

type RouteAuthType = "admin" | "guest" | "owner" | "user" | PermissionKeys;

const routeList: Array<[PathParameters, RouteType]> = [
  [
    ["up.root", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/Root")),
    },
  ],
  [
    ["up.auth.login", {}],
    {
      auth: "guest",
      component: lazy(async () => import("./pages/auth/LogIn")),
    },
  ],
  [
    ["up.auth.signUp", {}],
    {
      auth: "guest",
      component: lazy(async () => import("./pages/auth/SignUp")),
    },
  ],
  [
    [
      "up.auth.join",
      {
        teamDomain: ":teamDomain",
        teamUniqueIdentifier: ":teamUniqueIdentifier",
      },
    ],
    {
      component: lazy(async () => import("./pages/auth/JoinTeam")),
    },
  ],
  [
    ["up.auth.mfa.enable"],
    {
      auth: "user",
      component: lazy(async () => import("./pages/auth/EnableMfa")),
    },
  ],
  [
    ["up.auth.invites.show", { inviteToken: ":inviteToken" }],
    {
      component: lazy(async () => import("./pages/auth/Invites")),
    },
  ],
  [
    ["up.notifications.confirmation"],
    {
      component: lazy(async () => import("./pages/auth/NotificationConfirmation")),
    },
  ],
  [
    ["up.shortCuts.purchase"],
    {
      auth: "user",
      component: lazy(async () => import("./pages/onboarding/PurchaseRedirect")),
    },
  ],
  [
    ["up.shortCuts.install"],
    {
      auth: "user",
      component: lazy(async () => import("./pages/onboarding/InstallRedirect")),
    },
  ],
  [
    ["up.onboarding", { teamDomain: ":teamDomain" }],
    {
      children: [
        [
          ["up.onboarding.questions", { teamDomain: ":teamDomain" }],
          {
            auth: "owner",
            component: lazy(async () => import("./pages/onboarding/steps/Questions")),
          },
        ],
        [
          ["up.onboarding.bookCall", { teamDomain: ":teamDomain" }],
          {
            auth: "owner",
            component: lazy(async () => import("./pages/onboarding/steps/BookCall")),
          },
        ],
        [
          ["up.onboarding.inviteTeam", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageUsers",
            component: lazy(async () => import("./pages/onboarding/steps/InviteTeam")),
          },
        ],
        [
          ["up.onboarding.purchase", { teamDomain: ":teamDomain" }],
          {
            auth: "canPurchase",
            component: lazy(async () => import("./pages/onboarding/steps/Purchase")),
          },
        ],
        [
          [
            "up.onboarding.installCobrowsing",
            { teamDomain: ":teamDomain", teamUniqueIdentifier: ":teamUniqueIdentifier" },
          ],
          {
            component: lazy(async () => import("./pages/onboarding/steps/CobrowsingInstall")),
          },
        ],
      ],
      component: lazy(async () => import("./pages/onboarding/Onboarding")),
    },
  ],
  [
    ["up.invoice.showFailed", { invoiceId: ":invoiceId" }],
    {
      component: lazy(async () => import("./pages/settings/billing/PaymentFailed")),
    },
  ],
  [
    ["hs.extension.welcome"],
    {
      component: lazy(async () => import("./pages/helloscreen/extension/Welcome")),
    },
  ],
  [
    ["hs.extension.uninstall", {}],
    {
      component: lazy(async () => import("./pages/helloscreen/extension/Uninstall")),
    },
  ],
  [
    ["hs.visitorsList", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/Root")),
    },
  ],
  [
    ["hs.visitorsList", { searchTerm: ":searchTerm" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/VisitorsList")),
    },
  ],
  [
    ["hs.visitorsList", { searchTerm: "" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/VisitorsList")),
    },
  ],
  [
    ["hs.visitorsList", { isEmbedded: true }],
    {
      component: lazy(async () => import("./pages/helloscreen/VisitorsList")),
    },
  ],
  [
    ["hs.visitorsList", { isEmbedded: true, searchTerm: ":searchTerm" }],
    {
      component: lazy(async () => import("./pages/helloscreen/VisitorsList")),
    },
  ],
  [
    ["hs.visitorsList", { isEmbedded: true, searchTerm: "" }],
    {
      component: lazy(async () => import("./pages/helloscreen/VisitorsList")),
    },
  ],
  [
    ["hs.go", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/CobrowseGo")),
    },
  ],
  [
    ["hs.go", { isEmbedded: true }],
    {
      component: lazy(async () => import("./pages/helloscreen/CobrowseGo")),
    },
  ],
  [
    ["hs.cobrowse", { visitorId: ":visitorId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/Cobrowse")),
    },
  ],
  [
    ["hs.cobrowse", { isEmbedded: true, visitorId: ":visitorId" }],
    {
      component: lazy(async () => import("./pages/helloscreen/Cobrowse")),
    },
  ],
  [
    ["hs.cobrowse", { onPremiseTeamUniqueIdentifier: ":onPremiseTeamUniqueIdentifier", visitorId: ":visitorId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/helloscreen/CobrowseOnpremRedirect")),
    },
  ],
  [
    ["uv.visitorsList", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.visitorsList", { searchTerm: ":searchTerm" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.visitorsList", { searchTerm: "" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.visitorsList", { isEmbedded: true }],
    {
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.visitorsList", { isEmbedded: true, searchTerm: ":searchTerm" }],
    {
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.visitorsList", { isEmbedded: true, searchTerm: "" }],
    {
      component: lazy(async () => import("./pages/userview/VisitorsList")),
    },
  ],
  [
    ["uv.go", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/CobrowseGo")),
    },
  ],
  [
    ["uv.go", { isEmbedded: true }],
    {
      component: lazy(async () => import("./pages/userview/CobrowseGo")),
    },
  ],
  [
    ["uv.cobrowse", { visitorId: ":visitorId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/Cobrowse")),
    },
  ],
  [
    ["uv.cobrowse", { isEmbedded: true, visitorId: ":visitorId" }],
    {
      component: lazy(async () => import("./pages/userview/Cobrowse")),
    },
  ],
  [
    ["uv.cobrowse", { onPremiseTeamUniqueIdentifier: ":onPremiseTeamUniqueIdentifier", visitorId: ":visitorId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/userview/CobrowseOnpremRedirect")),
    },
  ],
  [
    ["up.reporting.usage", { teamDomain: ":teamDomain" }],
    {
      auth: "canViewReporting",
      component: lazy(async () => import("./pages/reporting/Usage")),
    },
  ],
  [
    ["up.visitorData", { teamDomain: ":teamDomain" }],
    {
      auth: "canManageContent",
      component: lazy(async () => import("./pages/settings/team/DeleteVisitorData")),
    },
  ],
  [
    ["up.settings.personal"],
    {
      auth: "user",
      component: lazy(async () => import("./pages/settings/Personal")),
    },
  ],
  [
    ["up.settings.manageTeams", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/settings/ManageTeams")),
    },
  ],
  [
    ["up.members", { teamDomain: ":teamDomain" }],
    {
      auth: "canManageUsers",
      children: [
        [
          ["up.members", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageUsers",
            component: lazy(async () => import("./pages/settings/team/members/MembersList")),
          },
        ],
        [
          ["up.members.invites", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageUsers",
            component: lazy(async () => import("./pages/settings/team/members/Invites")),
          },
        ],
      ],
      component: lazy(async () => import("./pages/settings/team/members/Index")),
    },
  ],
  [
    ["up.history", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/activity/History")),
    },
  ],

  [
    ["up.activity.activityLog", { teamDomain: ":teamDomain" }],
    {
      auth: "canViewReporting",
      component: lazy(async () => import("./pages/activity/ActivityLog")),
    },
  ],
  [
    ["up.settings.general", { teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/General")),
    },
  ],
  [
    ["up.settings.quickEnable", { setting: ":setting", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/general/QuickEnable")),
    },
  ],
  [
    ["up.settings.authTokens.new"],
    {
      auth: "user",
      component: lazy(async () => import("./pages/auth/IntegrationAccessToken")),
    },
  ],
  // Further /settings/teams/:teamDomain/xyz routes need to be added here
  [
    ["up.integrations", { teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Index")),
    },
  ],
  [
    ["up.integrations.zapier", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Zapier")),
    },
  ],
  [
    ["up.integrations.zendesk", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Zendesk")),
    },
  ],
  [
    ["up.integrations.freshchat", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Freshchat")),
    },
  ],
  [
    ["up.integrations.intercom.connect", { teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/IntercomConnect")),
    },
  ],
  [
    ["up.integrations.front", { teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Front")),
    },
  ],
  [
    ["up.integrations.front", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Front")),
    },
  ],
  [
    ["up.integrations.intercom.settings", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Intercom")),
    },
  ],
  [
    ["up.integrations.salesforce.settings", { integrationId: ":integrationId", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/integrations/Salesforce")),
    },
  ],
  [
    ["up.settings.general", { section: ":section", teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      component: lazy(async () => import("./pages/settings/General")),
    },
  ],
  [
    ["up.settings.billing", { teamDomain: ":teamDomain" }],
    {
      auth: "canManageBilling",
      children: [
        [
          ["up.settings.billing", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageBilling",
            component: lazy(async () => import("./pages/settings/billing/Billing")),
          },
        ],
        [
          ["up.settings.billing.sessionCredit", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageBilling",
            component: lazy(async () => import("./pages/settings/billing/SessionCredit")),
          },
        ],
        [
          ["up.settings.billing.subaccounts", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageBilling",
            component: lazy(async () => import("./pages/settings/billing/Subaccounts")),
          },
        ],
        [
          ["up.settings.billing.history", { teamDomain: ":teamDomain" }],
          {
            auth: "canManageBilling",
            component: lazy(async () => import("./pages/settings/billing/History")),
          },
        ],
      ],
      component: lazy(async () => import("./pages/settings/billing/Index")),
    },
  ],
  [
    ["up.settings.billing.upgrade", { feature: null as any, teamDomain: ":teamDomain" }],
    {
      auth: "canManageBilling",
      component: lazy(async () => import("./pages/settings/billing/Upgrade")),
    },
  ],
  [
    ["co.root", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/cobrowsingapi/Redirect")),
    },
  ],
  [
    ["co.cobrowse", { visitorId: ":visitorId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/cobrowsingapi/CobrowseInstructions")),
    },
  ],
  [
    ["co.dashboard", { teamDomain: ":teamDomain" }],
    {
      auth: "canAccessSettings",
      children: [
        [
          ["co.dashboard.restKeys", { teamDomain: ":teamDomain" }],
          {
            component: lazy(async () => import("./pages/cobrowsingapi/dashboard/RestKeys")),
          },
        ],
        [
          ["co.dashboard.requestHistory", { teamDomain: ":teamDomain" }],
          {
            component: lazy(async () => import("./pages/cobrowsingapi/dashboard/RequestHistory")),
          },
        ],
        [
          ["co.dashboard.webhookHistory", { teamDomain: ":teamDomain" }],
          {
            component: lazy(async () => import("./pages/cobrowsingapi/dashboard/WebhookHistory")),
          },
        ],
      ],
      component: lazy(async () => import("./pages/cobrowsingapi/Dashboard")),
    },
  ],
  [
    ["hq.root"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/Root")),
    },
  ],
  [
    ["hq.emailRedirect"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/EmailRedirect")),
    },
  ],
  [
    ["hq.accounts.index", {}],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/accounts/Accounts")),
    },
  ],
  [
    ["hq.accounts.show", { companyId: ":companyId" }],
    {
      auth: "admin",
      children: [
        [
          ["hq.accounts.show", { companyId: ":companyId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Overview")),
          },
        ],
        [
          ["hq.accounts.show.team", { companyId: ":companyId", teamId: ":teamId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Team")),
          },
        ],
        [
          ["hq.accounts.show.members", { companyId: ":companyId", teamId: ":teamId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Members")),
          },
        ],
        [
          ["hq.accounts.show.usage", { companyId: ":companyId", teamId: ":teamId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Usage")),
          },
        ],
        [
          ["hq.accounts.show.emails", { companyId: ":companyId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Emails")),
          },
        ],
        [
          ["hq.accounts.show.contacts", { companyId: ":companyId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/Contacts")),
          },
        ],
        [
          ["hq.accounts.show.companyDocuments", { companyId: ":companyId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/CompanyDocuments")),
          },
        ],
        [
          ["hq.accounts.show.activityLog", { companyId: ":companyId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/ActivityLog")),
          },
        ],
        [
          ["hq.accounts.show.charges", { companyId: ":companyId", teamId: ":teamId" }],
          {
            component: lazy(async () => import("./pages/hq/accounts/show/ChargesAndInvoices")),
          },
        ],
      ],
      component: lazy(async () => import("./pages/hq/accounts/Show")),
    },
  ],
  [
    ["hq.customerSuccess.index"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/cs/Index")),
    },
  ],
  [
    ["hq.customerSuccess.list", { stage: ":stage" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/cs/List")),
    },
  ],
  [
    ["hq.aiChanges"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/AiChanges")),
    },
  ],
  [
    ["hq.sales.index"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/Index")),
    },
  ],
  [
    ["hq.sales.activities"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/Activities")),
    },
  ],
  [
    ["hq.sales.opportunities.index"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/opportunities/Index")),
    },
  ],
  [
    ["hq.sales.opportunities.show", { opportunityId: ":opportunityId" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/opportunities/Show")),
    },
  ],
  [
    ["hq.sales.tasks"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/Tasks")),
    },
  ],
  [
    ["hq.sales.leads"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/sales/LeadList")),
    },
  ],
  [
    ["hq.quotes.index"],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/quotes/Index")),
    },
  ],
  [
    ["hq.quotes.show", { quotePublicKey: ":quotePublicKey" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/quotes/Show")),
    },
  ],
  [
    ["up.quotes.show", { quotePublicKey: ":quotePublicKey" }],
    {
      component: lazy(async () => import("./pages/quotes/Show")),
    },
  ],
  [
    ["hq.quotes.transfer.new", { quotePublicKey: ":quotePublicKey" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/quotes/Transfer")),
    },
  ],
  [
    ["hq.users.show", { userId: ":userId" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/users/Show")),
    },
  ],
  [
    ["ld.root", {}],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/Root")),
    },
  ],
  [
    ["ld.leads.index", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/leads/List")),
    },
  ],
  [
    ["ld.links.list", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/links/List")),
    },
  ],
  [
    ["ld.links.create", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/links/Create")),
    },
  ],
  [
    ["ld.templates.index", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/templates/Index")),
    },
  ],
  [
    ["ld.templates.create", { teamDomain: ":teamDomain" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/templates/Create")),
    },
  ],
  [
    ["ld.templates.show", { teamDomain: ":teamDomain", templateId: ":templateId" }],
    {
      auth: "user",
      component: lazy(async () => import("./pages/livedocument/user/templates/Show")),
    },
  ],
  [
    ["ld.links.show", { linkSlug: ":linkSlug" }],
    {
      component: lazy(async () => import("./pages/livedocument/ShowLink")),
    },
  ],
  [
    ["ld.links.show", { linkSlug: ":linkSlug", section: ":section" } as any],
    {
      component: lazy(async () => import("./pages/livedocument/ShowLink")),
    },
  ],
  [
    ["hq.globalActivityLog", {}],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/reporting/GlobalActivityLog")),
    },
  ],
  [
    ["hq.debug.show", { visitorId: ":visitorId" }],
    {
      auth: "admin",
      component: lazy(async () => import("./pages/hq/Debug")),
    },
  ],
  [
    ["hs.extension.view", { sessionCode: ":sessionCode" }], // This has to be placed last as it's a catch-all route
    {
      component: lazy(async () => import("./pages/helloscreen/extension/View")),
    },
  ],
];

export function getRoutes(): Array<[string, RouteType]> {
  return routeList
    .filter(([path, route]: [PathParameters, RouteType]) => {
      return getRouteOrigin(path[0]) === location.origin;
    })
    .map(([pathParam, route]: [PathParameters, RouteType]) => {
      return [path(...pathParam).split("?")[0]!, route];
    });
}
